import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { isEmptyArray, isStringEmpty } from '@common/helpers';
import {
  FARE_RULE_TABLE_ITEM_LABEL,
  STAY_PERIOD,
} from '@common/helpers/constants/my-booking/my_booking_itinerary.contant';
import {
  ChangeCondition,
  PassengerTypeFareCondition,
  RefundCondition,
} from '@common/interfaces/fare-conditions/fare-conditions-passenger-type.interface';
import { FFPriorityCodeI18nAll } from '@common/interfaces/master/ff_priority_code_i18n_all';
import { MasterDataS01P031 } from '@common/interfaces/mybooking-itinerary/mybooking-itinerary.interface';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { MasterStoreKey } from '@conf/asw-master.config';
import { SupportModalBlockComponent } from '@lib/components/support-class';
import { AswMasterModel, MOffice } from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription, take } from 'rxjs';
import {
  FareConditionsResponseData,
  FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner,
  GetOrderResponseData,
  GetOrderResponseDataAir,
  Traveler,
} from 'src/sdk-servicing';

@Component({
  selector: 'asw-fare-rules-detail',
  templateUrl: './fare-rules-detail.component.html',
  styleUrls: ['./fare-rules-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FareRulesDetailComponent extends SupportModalBlockComponent {
  //サブページID
  subPageId: string = '';
  //サブ機能ID
  subFunctionId: string = '';

  ffName: string = '';
  fBasis: string = '';

  mOffice!: MOffice;

  readonly MAX_STAY_PERIOD = STAY_PERIOD.MAX;
  readonly MIN_STAY_PERIOD = STAY_PERIOD.MIN;

  private _FFPriorityCodeAll: Array<FFPriorityCodeI18nAll> = [];
  private _subscription = new Subscription();

  override payload!: {
    fareConditions: FareConditionsResponseData;
    orderData: GetOrderResponseData;
  };

  constructor(
    private _common: CommonLibService,
    private _myBookingItineraryService: MyBookingItineraryService,
    public _staticMsg: StaticMsgPipe,
    private _change: ChangeDetectorRef
  ) {
    super(_common);
  }

  init(): void {
    this._subscription.add(
      this._myBookingItineraryService.masterData$.subscribe((result: MasterDataS01P031) => {
        this._FFPriorityCodeAll = result?.fFPriorityCodeAll ?? [];
        this.mOffice = result?.mOffice && result.mOffice.length > 0 ? result.mOffice[0] : ({} as MOffice);
        this._change.markForCheck();
      })
    );
  }

  destroy(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  refresh(): void {}

  reload(): void {}

  // 提携：FareRuleSliderComponentをres spaのBlockSliderComponentに置き換える
  //  /** 右にスクロールできるスライダーです */
  //  get isCanScrollRight(): boolean {
  //    return Boolean(Number(this.payload?.fareConditions?.airOfferConditions?.airOfferConditionBounds?.length) > 2);
  //  }

  /** 賞品予約です */
  get isAwardBooking(): boolean {
    return Boolean(this.payload?.orderData?.orderType?.isAwardBooking);
  }

  /** (26) 「閉じる(運賃ルール詳細モーダル)」ボタン押下時 */
  clickClose() {
    // 1.	運賃ルール詳細モーダルを閉じる。
    this.close();
  }

  /** [1.10.1] 運賃ルール詳細モーダル */
  get showContentModal(): boolean {
    return this._myBookingItineraryService.showContentModalFareRules(
      this.payload?.orderData,
      this.payload?.fareConditions
    );
  }

  /** [1.10.5] 搭乗者種別 */
  getPassengerTypeConvertLabel(traveler?: Traveler): string {
    return this._myBookingItineraryService.getPassengerTypeLabel(traveler?.passengerTypeCode);
  }

  /** [1.10.6] 出発地 */
  getPointOfDeparture(
    conditionBound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner
  ): string {
    return this._myBookingItineraryService.getAirportLocationName(
      conditionBound?.originLocationCode,
      conditionBound?.originLocationName
    );
  }

  /** [1.10.7] 到着地 */
  getPlaceOfArrival(conditionBound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner): string {
    return this._myBookingItineraryService.getAirportLocationName(
      conditionBound?.destinationLocationCode,
      conditionBound?.destinationLocationName
    );
  }

  /** [1.10.24] FF名称ラベル */
  get ffNameLabel(): string {
    return this._myBookingItineraryService.getFFNameLabel(this.isAwardBooking);
  }

  checkFareSection(airOfferConditionBound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner) {
    /** [1.10.8] FF名称 */
    this.ffName = this._myBookingItineraryService.getFFName(airOfferConditionBound, this.payload?.orderData);
    /** [1.10.9] Fare Basis */
    this.fBasis = this.fareBasis(airOfferConditionBound);
    if (isStringEmpty(this.ffName) && isStringEmpty(this.fBasis)) {
      return false;
    }
    return true;
  }

  /** [1.10.9] Fare Basis 運賃基準: FF名称にて、利用運賃が表示できない旨を表示している場合、非表示とする。*/
  fareBasis(airOfferConditionBound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner): string {
    return this.ffName !== this._staticMsg.transform(FARE_RULE_TABLE_ITEM_LABEL.HYPHEN)
      ? String(airOfferConditionBound?.fareBasis ?? '')
      : '';
  }

  /**
   * [1.10.11] 運賃詳細リンク => 旅程の1便目の出発日(※1)
   * ※1:PNR情報取得API応答結果.data.air.bounds[]の内に、originLocationCode=当該運賃ルール用のバウンド.originLocationCode要素がある場合、旅程の1便目の出発日にoriginDepartureDateTimeを設定する。
   */
  originDepartureDateTime(
    fareBound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner
  ): string {
    let originDepartureDate: string = '';
    this.payload?.orderData?.air?.bounds?.forEach((bound) => {
      if (bound?.originLocationCode === fareBound?.originLocationCode) {
        originDepartureDate = bound?.originDepartureDateTime ?? '';
      }
    });

    return originDepartureDate;
  }

  /** [1.10.11] 運賃詳細リンク */
  fareDetailsLink(fareBound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner): string {
    /** 提携: 固定で新国内運賃へのコンテンツリンクを表示する。*/
    return 'label.domestic.fareDetails.link';
  }

  /** [1.10.12] 変更ルール */
  getChangeCondition(boundIndex: number, passengerTypeCode?: string): Array<ChangeCondition> {
    /** 1)運賃ルール・手荷物情報取得API結果.data.airOfferCondition.<搭乗者種別>.changeCondition[]が存在しない場合、非表示とする。*/
    const passengerTypeFareCondition =
      (this.payload?.fareConditions?.airOfferConditions?.[String(passengerTypeCode)] as PassengerTypeFareCondition) ??
      ({} as PassengerTypeFareCondition);
    // 往路と復路が混在している変更ルール
    const allFareConditions = passengerTypeFareCondition?.changeConditions ?? ([] as Array<ChangeCondition>);
    // boundIndexにより混在せずに往路又は復路のみの変更ルール配列を返す
    return allFareConditions[boundIndex] ? [allFareConditions[boundIndex]] : [];
  }

  /** [1.10.12] 変更ルール */
  showChangeCondition(boundIndex: number, passengerTypeCode?: string): boolean {
    /** 2)運賃ルール・手荷物情報取得API結果.data.airOfferCondition.<搭乗者種別>.changeCondition[]が存在する場合 */
    return !isEmptyArray(this.getChangeCondition(boundIndex, passengerTypeCode));
  }

  /** [1.10.13] 出発前 (変更ルール) */
  getBeforeDeparture(changeCondition?: ChangeCondition): string {
    let beforeDeparture: string = '';
    /** 1)当該変更ルール.beforeDeparture.defaultDescription≠""(空欄)の場合、当該変更ルール.beforeDeparture.defaultDescriptionを表示する。*/
    if (!isStringEmpty(changeCondition?.beforeDeparture?.defaultDescription)) {
      beforeDeparture = String(changeCondition?.beforeDeparture?.defaultDescription);
    }
    /** 2)上記以外の場合、""(空欄)を表示する */
    return beforeDeparture;
  }

  /** [1.10.14] no show (変更ルール) */
  getBeforeDepartureNoShow(changeCondition?: ChangeCondition): string {
    let departureNoShow: string = '';
    /** 1)当該変更ルール.beforeDepartureNoShow.defaultDescription≠""(空欄)の場合、当該変更ルール.beforeDepartureNoShow.defaultDescriptionを表示する。*/
    if (!isStringEmpty(changeCondition?.beforeDepartureNoShow?.defaultDescription)) {
      departureNoShow = String(changeCondition?.beforeDepartureNoShow?.defaultDescription);
    }
    /** 2)上記以外の場合、""(空欄)を表示する */
    return departureNoShow;
  }

  /** [1.10.15] 一部搭乗済み (変更ルール) */
  getAfterDeparture(changeCondition?: ChangeCondition): string {
    let afterDeparture: string = '';
    /** 1)当該変更ルール.afterDeparture.defaultDescription≠""(空欄)の場合、当該変更ルール.afterDeparture.defaultDescriptionを表示する。*/
    if (!isStringEmpty(changeCondition?.afterDeparture?.defaultDescription)) {
      afterDeparture = String(changeCondition?.afterDeparture?.defaultDescription);
    }
    /** 2)上記以外の場合、""(空欄)を表示する */
    return afterDeparture;
  }

  /** [1.10.16] no show(一部搭乗済み) (変更ルール) */
  getAfterDepartureNoShow(changeCondition?: ChangeCondition): string {
    let afterDepartureNoShow: string = '';
    /** 1)当該変更ルール.afterDepartureNoShow.defaultDescription≠""(空欄)の場合、当該変更ルール.afterDepartureNoShow.defaultDescriptionを表示する。*/
    if (!isStringEmpty(changeCondition?.afterDepartureNoShow?.defaultDescription)) {
      afterDepartureNoShow = String(changeCondition?.afterDepartureNoShow?.defaultDescription);
    }
    /** 2)上記以外の場合、""(空欄)を表示する */
    return afterDepartureNoShow;
  }

  /** [1.10.17] 払戻ルール */
  getRefundCondition(boundIndex: number, passengerTypeCode?: string): Array<RefundCondition> {
    const passengerTypeFareCondition =
      (this.payload?.fareConditions?.airOfferConditions?.[String(passengerTypeCode)] as PassengerTypeFareCondition) ??
      ({} as PassengerTypeFareCondition);
    // 往路と復路が混在している払戻ルール
    const allRefundConditions = passengerTypeFareCondition?.refundConditions ?? ([] as Array<RefundCondition>);
    // boundIndexにより混在せずに往路又は復路のみの払戻ルール配列を返す
    return allRefundConditions[boundIndex] ? [allRefundConditions[boundIndex]] : [];
  }

  /** [1.10.17] 払戻ルール */
  showRefundCondition(boundIndex: number, passengerTypeCode?: string): boolean {
    /** 運賃ルール・手荷物情報取得API結果.data.airOfferCondition.<搭乗者種別>.refundCondition[]が存在する場合 */
    return !isEmptyArray(this.getRefundCondition(boundIndex, passengerTypeCode));
  }

  /** [1.10.18] 出発前 (払戻ルール) */
  getBeforeDepartureRefund(refundCondition?: RefundCondition): string {
    let beforeDepartureRefund: string = '';
    /** 1)当該払戻ルール.beforeDeparture.defaultDescription≠""(空欄)の場合、当該払戻ルール.beforeDeparture.defaultDescriptionを表示する。*/
    if (!isStringEmpty(refundCondition?.beforeDeparture?.defaultDescription)) {
      beforeDepartureRefund = String(refundCondition?.beforeDeparture?.defaultDescription);
    }
    /** 2)上記以外の場合、""(空欄)を表示する */
    return beforeDepartureRefund;
  }

  /** [1.10.19] no show (払戻ルール) */
  getBeforeDepartureNoShowRefund(refundCondition?: RefundCondition): string {
    let beforeDepartureNoShowRefund: string = '';
    /** 1)当該払戻ルール.beforeDepartureNoShow.defaultDescription≠""(空欄)の場合、当該払戻ルール.beforeDepartureNoShow.defaultDescriptionを表示する。*/
    if (!isStringEmpty(refundCondition?.beforeDepartureNoShow?.defaultDescription)) {
      beforeDepartureNoShowRefund = String(refundCondition?.beforeDepartureNoShow?.defaultDescription);
    }
    /** 2)上記以外の場合、""(空欄)を表示する */
    return beforeDepartureNoShowRefund;
  }

  /** [1.10.20] 一部搭乗済み (払戻ルール) */
  getAfterDepartureRefund(refundCondition?: RefundCondition): string {
    let afterDepartureRefund: string = '';
    /** 1)当該払戻ルール.afterDeparture.defaultDescription≠""(空欄)の場合、当該払戻ルール.afterDeparture.defaultDescriptionを表示する。*/
    if (!isStringEmpty(refundCondition?.afterDeparture?.defaultDescription)) {
      afterDepartureRefund = String(refundCondition?.afterDeparture?.defaultDescription);
    }
    /** 2)上記以外の場合、""(空欄)を表示する */
    return afterDepartureRefund;
  }

  /** [1.10.21] no show(一部搭乗済み) (払戻ルール) */
  getAfterDepartureNoShowRefund(refundCondition?: RefundCondition): string {
    let afterDepartureNoShowRefund: string = '';
    /** 1)当該払戻ルール.afterDepartureNoShow.defaultDescription≠""(空欄)の場合、当該払戻ルール.afterDepartureNoShow.defaultDescriptionを表示する。*/
    if (!isStringEmpty(refundCondition?.afterDepartureNoShow?.defaultDescription)) {
      afterDepartureNoShowRefund = String(refundCondition?.afterDepartureNoShow?.defaultDescription);
    }
    return afterDepartureNoShowRefund;
  }
}
